/**
 * index.jsx
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

import 'core-js/stable'
import 'regenerator-runtime/runtime'

import React from 'react'
import MainContainer from 'Containers/MainContainer'
import { createRoot } from 'react-dom/client'
import '@formatjs/intl-pluralrules/polyfill'
// import '@formatjs/intl-pluralrules/locale-data/en'
import '@formatjs/intl-pluralrules/locale-data/it'
import { createAuthRoutes, createUnauthRoutes } from 'Route/routes'
import { getBankFromPath } from 'Utility/bankMethods'
import { redirectToLogin, setFaviconForTheme } from 'Utility/globalMethods'
import history from 'Utility/history'
import { initLogger } from 'Utility/logger'
import { BANKS_FOR_THEME } from 'Schemas/constants'
import { routesList } from 'Schemas/routesList'
import store from 'Store/createStore'

if (process.env.NODE_ENV === 'development') {
  import('sa11y/dist/js/lang/en').then((sa11yLang) => {
    import('sa11y/dist/js/sa11y.esm').then((sa11yModule) => {
      sa11yModule.Lang.addI18n(sa11yLang.default.strings)
      // eslint-disable-next-line no-unused-vars
      const sa11y = new sa11yModule.Sa11y({
        checkRoot: 'body'
      })
    })
  })
}

// Get the theme from the URL path
const bank = getBankFromPath()
// Default to nexi theme if bank not found (case: uci present in bankServiceMapping but not in BANKS_FOR_THEME)
const theme = BANKS_FOR_THEME?.[bank] || BANKS_FOR_THEME.nexi
document.documentElement.setAttribute('data-theme', theme)
setFaviconForTheme(theme)

// ========================================================
// Render Setup
// ========================================================
const routes = Object.fromEntries(Object.entries(process.env).filter(([key]) => key.includes('ROUTE')))
const filter = Object.keys(routes).filter((a) => routes[a] === 'true')
const MOUNT_NODE = document.getElementById('root')
const authRoutes = createAuthRoutes()
const unauthRoutes = createUnauthRoutes()
const root = createRoot(MOUNT_NODE)
const regex = new RegExp(`(${filter.join('|').replace(/route_/gi, '')})`, 'i')
authRoutes.routes = authRoutes.routes.filter((a) => regex.test(a.key))
unauthRoutes.routes = unauthRoutes.routes.filter((a) => regex.test(a.key))

let logged = false
if (window.sessionStorage.getItem('jwt')) {
  logged = true
}
if (process.env.DEBUG_MODE === 'true') {
  initLogger()
}

if (process.env.ISMOCK) {
  window.sessionStorage.setItem(
    'jwt',
    JSON.stringify('Bearer asc7gdsv0b8s7vg8iubcpugdcs7o8gcioV8SACC.KDSF9SPVASDdsf8sad90fF')
  )
  window.sessionStorage.setItem('pkce_state', 'mocked_state')
  window.sessionStorage.setItem('pkce_code_verifier', 'mocked_code_verifier')
  logged = true
} else if (
  !logged &&
  window.location.pathname !== routesList.goToLogin &&
  !unauthRoutes.routes.some(
    (el) => `/${el.key.toLowerCase()}` === bank ? window.location.pathname.toLowerCase().replace(/^\/[^\/]+/, '').replace(/-/g, '') : window.location.pathname.toLowerCase().replace(/-/g, ''))
) {
  redirectToLogin()
}

root.render(
  <MainContainer store={store} authRoutes={authRoutes} unauthRoutes={unauthRoutes} logged={logged} history={history} />
)

if (module.hot) {
  module.hot.accept('Route/routes', () =>
    setImmediate(() => {
      root.render(
        <MainContainer
          store={store}
          authRoutes={authRoutes}
          unauthRoutes={unauthRoutes}
          logged={logged}
          history={history}
        />
      )
    })
  )
}

if (process.env.NODE_ENV === 'production') {
  if (!window.console) window.console = {}
  const methods = ['log', 'debug', 'warn', 'info', 'error']
  for (let i = 0; i < methods.length; i += 1) {
    console[methods[i]] = () => {}
  }
  require('@lcdp/offline-plugin/runtime').install()
}
